import { Iso } from 'iso-fns2'

/**
 * ADDING A FEATURE FLAG
 * 1. Add a new object to the operatorGroupFeatureFlags array below.
 * 2. The name should be in PascalCase
 * 3. The created date should be now (you can use the dpkshrma.insert-iso-timestamp VS Code extension to help)
 */

/**
 * REMOVING A FEATURE FLAG
 * 1. Set the retirementDate property to now in codebase (you can use the dpkshrma.insert-iso-timestamp VS Code extension to help). This cause a TypeError anywhere this flag used in the codebase
 * 2. Remove all references to the flag in the codebase
 * 3. Wait for this change to be deployed to prod.
 * 4. Remove this feature flag from the database using a db migration (See query at bottom of packages/services/src/outfitter-account-management/outfitter-group/operator-group-db-data-provider.ts)
 * 5. Remove this feature flag from the operatorGroupFeatureFlags array below
 */

export const operatorGroupFeatureFlags = [
  {
    name: 'HQRequireMFAForOwner',
    description:
      'This is required to be true for an owner who will have access to banking details in HQ. It can be set to false during onboarding temporarily.',
    createdDate: '2024-12-01T03:26:34.752Z'
  },
  {
    name: 'MigratingReservations',
    description:
      'Causes emails to NOT be sent when creating reservations for an outfitter. Intended for temporary use when migrating an outfitter from another system.',
    createdDate: '2024-11-21T18:22:28.051Z'
  },
  {
    name: 'HQSidebarCheckInItem',
    description:
      'Adds a new check-in item to the sidebar. Allows outfitters to process vehicle check-ins by selecting the vehicle first (rather than selecting a reservation.',
    createdDate: '2025-01-03T03:46:32.976Z'
  },
  {
    name: 'CustomHQMessageTemplates',
    description: 'Allows access to the Custom Message Templates features set within HQ.',
    createdDate: '2025-01-28T07:27:23.000Z'
  },
  {
    name: 'HQS700Support',
    description: 'Allows access to the s700 internet terminal within HQ.',
    createdDate: '2025-01-31T07:27:23.000Z'
  },
  {
    name: 'Viator',
    description: 'Allows access to viator module within HQ.',
    createdDate: '2025-02-10T21:56:00.939Z'
  },
  {
    name: 'OutfitterSupportForWebAnalytics',
    description:
      'Allows Outfitters to add Google Analytics and IDs to HQ, which extends the reach of these accounts into adventures.polaris.com.',
    createdDate: '2025-02-04T12:09:12.000Z'
  },
  {
    name: 'GiftCards',
    description: 'Gift cards support in hq',
    createdDate: '2025-02-04T12:09:12.000Z'
  },
  {
    name: 'HQConfigurableTurnTime',
    description:
      'Allows Outfitters to have a configurable time frame they can set to leave a designated time between bookings for vehicle turn around time within HQ',
    createdDate: '2025-03-12T11:14:26.874Z'
  },
  {
    name: 'HQReservationCardsOnFileAndPaymentSplit',
    description: 'Allows Outfitters to add named cards on file to reservations and perform split payment within HQ.',
    createdDate: '2025-03-17T09:31:00.000Z'
  },
  {
    name: 'HQAllowOverridePricing',
    description: 'Allows Outfitters to override pricing on a rental line item with order editing.',
    createdDate: '2025-04-08T18:43:26.874Z'
  },

  {
    name: 'UseCustomerWebWaivers',
    description: 'Manages redirect behavior to Customer Web Waiver flow.',
    createdDate: '2025-03-26T15:50:55.636Z'
  },
  {
    name: 'ReservationFocusCalendar',
    description: 'Allows users to view calendar events grouped by some field(f.e. "orderId", "clusterId")',
    createdDate: '2025-04-08T17:56:03.036Z'
  },
  {
    name: 'RelativePricingConfiguration',
    description:
      'Allows outfitters to configure prices rules as a relative change from existing pricing. (ie. + 10% or -$20)',
    createdDate: '2025-04-14T18:39:34.748Z'
  },
  {
    name: 'DynamicPricingConfiguration',
    description:
      'Allows outfitters to configure prices rules based on how far in advance a customer books or the number of vehicles available the time of booking',
    createdDate: '2025-04-14T18:50:11.721Z'
  },
  {
    name: 'Zapier',
    description: 'Allows outfitters to get zapier api keys to build their own zaps',
    createdDate: '2025-04-17T17:20:00.499Z'
  },
  {
    name: 'HQRequiredRentalAddOns',
    description: 'Allows outfitters to set rental add-ons as required for a reservation.',
    createdDate: '2025-04-21T17:20:00.499Z'
  },
  {
    name: 'ArrivalReservations',
    description: 'Enables the Waivers HQ page showing completed waivers and currently checked out vehicles.',
    createdDate: '2025-05-16T16:18:04.205Z'
  },
  {
    name: 'HQCheckoutsCanTriggerTransfers',
    description:
      'HQ Checkouts will display all vehicle across the group, with special emphasis on those that belong to the selected outfitter.  Choosing a vehicle from another outfitter will trigger a transfer of the vehicle.',
    createdDate: '2025-06-10T17:20:00.499Z'
  },
  {
    name: 'UseNewEmailDomains',
    description:
      'Emails will be sent to a selected Outfitter group using new domains and separated into transactional and marketing emails.',
    createdDate: '2025-06-27T13:51:00.205Z'
  }
] as const satisfies Readonly<FeatureFlagDefinition[]>

/**
 * DO NOT EDIT BELOW THIS LINE
 */

interface FeatureFlagDefinition {
  name: string
  description: string
  createdDate: Iso.Instant
  retirementDate?: Iso.Instant
}

type ExcludeNever<Tuple extends [...any[]]> = Tuple extends [infer F, ...infer R]
  ? [F] extends [never]
    ? ExcludeNever<R>
    : [F, ...ExcludeNever<R>]
  : []
type ExtractName<Tuple extends readonly [...{ name: string }[]]> = ExcludeNever<{
  -readonly [Index in keyof Tuple]: ExcludeRetired<Tuple[Index]>['name']
}>

export const operatorGroupFeatureFlagOptions = operatorGroupFeatureFlags
  .filter((f) => !f['retirementDate'])
  .map((f) => f.name) as unknown as ExtractName<typeof operatorGroupFeatureFlags>

type ExcludeRetired<T> = T extends { retirementDate: Iso.Instant } ? never : T

export type OperatorGroupFeatureFlag = ExcludeRetired<(typeof operatorGroupFeatureFlags)[number]>['name']

export function featureFlagsFilter(f: FeatureFlagDefinition['name']): boolean {
  const match = operatorGroupFeatureFlags.find((flag) => flag.name === f)
  if (match) {
    return !match['retirementDate']
  } else {
    return true
  }
}
